import * as React from 'react'

import { apiFetch, apiJson } from '@/lib/api'
import type { ReferenceVideo, StyleReference } from '@/types'

interface RefListPayload {
  items: ReferenceVideo[]
  total: number
}

interface StyleListPayload {
  items: StyleReference[]
  total: number
}

export function useReferences(pollMs = 10000) {
  const [references, setReferences] = React.useState<ReferenceVideo[]>([])
  const [loading, setLoading] = React.useState(true)
  const [error, setError] = React.useState<string | null>(null)

  const load = React.useCallback(async () => {
    try {
      const data = await apiJson<RefListPayload>('/api/v1/references?limit=200')
      setReferences(data.items)
      setError(null)
    } catch (err) {
      setError((err as Error).message)
    } finally {
      setLoading(false)
    }
  }, [])

  React.useEffect(() => {
    load()
    // Poll if any references are still processing
    const hasActive = references.some(
      (r) => r.analysis_status === 'pending' || r.analysis_status === 'processing'
    )
    const interval = setInterval(load, hasActive ? pollMs : pollMs * 4)
    return () => clearInterval(interval)
  }, [load, pollMs, references.length])

  return { references, loading, error, refresh: load }
}

export function useStyleReferences() {
  const [styles, setStyles] = React.useState<StyleReference[]>([])
  const [loading, setLoading] = React.useState(true)

  const load = React.useCallback(async () => {
    try {
      const data = await apiJson<StyleListPayload>('/api/v1/references/styles/all')
      setStyles(data.items)
    } catch {
      // Silently fail — non-critical
    } finally {
      setLoading(false)
    }
  }, [])

  React.useEffect(() => {
    load()
  }, [load])

  return { styles, loading, refresh: load }
}

export function useUploadReference() {
  const [uploading, setUploading] = React.useState(false)

  const upload = React.useCallback(async (file: File): Promise<ReferenceVideo> => {
    setUploading(true)
    try {
      const formData = new FormData()
      formData.append('file', file)
      const response = await apiFetch('/api/v1/references/upload', {
        method: 'POST',
        body: formData,
      })
      if (!response.ok) {
        const data = await response.json().catch(() => ({}))
        throw new Error(data.detail || `Upload failed (${response.status})`)
      }
      return (await response.json()) as ReferenceVideo
    } finally {
      setUploading(false)
    }
  }, [])

  const addUrl = React.useCallback(async (url: string, title?: string): Promise<ReferenceVideo> => {
    setUploading(true)
    try {
      const data = await apiJson<ReferenceVideo>('/api/v1/references/url', {
        method: 'POST',
        body: JSON.stringify({ source_url: url, title }),
      })
      return data
    } finally {
      setUploading(false)
    }
  }, [])

  return { upload, addUrl, uploading }
}
